import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "src/axiosInstance";
import { Avatar, Modal, MenuItem, Select, TextField } from '@mui/material';
import { BallTriangle } from 'react-loader-spinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NoRecordFound from 'src/components/NoRecordFound';
import { useAuth } from 'src/hooks/useAuth';
// import { BallTriangle } from 'react-loader-spinner';
// import Pagination from "../../../components/pagination";

function OrdersPage() {
  // All hooks at the top
  const { data: user } = useAuth();
  const [testModeModal, setTestModeModal] = useState(false);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    order_confirmation: '',
    payment_method: '',
    min_total_cost: '',
    max_total_cost: '',
    min_order_unit: '',
    max_order_unit: '',
    startDate: null,
    endDate: null,
  });

  // All useEffects at the top
  useEffect(() => {
    if (user && !user.onboarded) setTestModeModal(true);
  }, [user]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOrders(1, searchTerm);
    }, 400);
    setDebounceTimer(timer);
    // eslint-disable-next-line
  }, [searchTerm]);

  // Debug: log orders before rendering
  console.log('ORDERS ARRAY:', orders);

  const fetchOrders = async (page = currentPage, term = searchTerm, perPage = 10, filterVals = filters) => {
    setLoading(true);
    try {
      const params = { page, perpage: perPage, search: term };
      if (filterVals.status) params.status = filterVals.status;
      if (filterVals.order_confirmation) params.order_confirmation = filterVals.order_confirmation;
      if (filterVals.payment_method) params.payment_method = filterVals.payment_method;
      if (filterVals.min_total_cost) params.min_total_cost = filterVals.min_total_cost;
      if (filterVals.max_total_cost) params.max_total_cost = filterVals.max_total_cost;
      if (filterVals.min_order_unit) params.min_order_unit = filterVals.min_order_unit;
      if (filterVals.max_order_unit) params.max_order_unit = filterVals.max_order_unit;
      if (filterVals.startDate) params.dateFrom = filterVals.startDate;
      if (filterVals.endDate) params.dateTo = filterVals.endDate;
      const response = await axiosInstance.get("/shop/order/get-customer-orders", { params });
      const { data } = response.data;
      let ordersArray = [];
      if (Array.isArray(data?.data)) {
        ordersArray = data.data;
      } else if (data?.data && typeof data.data === 'object') {
        ordersArray = [data.data];
      }
      setOrders(ordersArray);
      setPaginationData(data?.pagination || {});
    } catch (error) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // After all hooks, do the conditional return
  if (user && !user.onboarded) {
    return (
      <Modal open={testModeModal} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Currently on Test Mode</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Complete your profile to perform other transactions
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                marginLeft: 4
              }}
              onClick={() => navigate('/app/profile?edit=1')}
            >
              Complete Profile
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Awaiting Approval modal logic
  if (user && user.onboarded && (user.is_approved !== 'verified' || !user.is_verified)) {
    return (
      <Modal open={true} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Awaiting Verification</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Your profile is currently being verified. This will take at least 24 hours.
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                marginLeft: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Back Home
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setFilterOpen(false);
    setCurrentPage(1);
    fetchOrders(1, searchTerm, 10, filters);
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      order_confirmation: '',
      payment_method: '',
      min_total_cost: '',
      max_total_cost: '',
      min_order_unit: '',
      max_order_unit: '',
      startDate: null,
      endDate: null,
    });
  };

  return (
    <div
      style={{
        background: "#fff",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
        maxWidth: 430,
        margin: "0 auto",
        padding: 0,
      }}
    >
      {/* Remove or reduce top padding */}
      {/* <div style={{ padding: "12px 0 10px 12px" }}></div> */}
      <div style={{ padding: "2px 0 4px 18px" }}>
        <div style={{ fontWeight: 700, fontSize: 23, color: "#222" }}>Orders</div>
        <div style={{ color: "#757575", fontSize: 14, marginTop: 6, marginBottom: 10 }}>
          A list of all your business sales
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", margin: "14px 12px 0 12px" }}>
        <div
          style={{
            flex: 1,
            background: "#fff",
            border: "1.5px solid #E0E0E0",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            height: 38,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
            <path d="M20 20l-3-3" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            style={{
              border: "none",
              outline: "none",
              fontSize: 15,
              marginLeft: 8,
              width: "100%",
              color: "#757575",
              background: "transparent",
            }}
          />
        </div>
        <div
          style={{
            width: 38,
            height: 38,
            background: "#fff",
            border: "1.5px solid #E0E0E0",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: 8,
            cursor: "pointer",
          }}
          onClick={() => setFilterOpen(true)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M7 12h10M10 18h4" stroke="#222" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 120 }}>
            <BallTriangle height={50} width={55} radius={5} color="#af3585" ariaLabel="ball-triangle-loading" visible />
          </div>
        ) : orders.length === 0 ? (
          <NoRecordFound message="No orders found." />
        ) : (
          orders.map((order, idx) => {
            const totalCost =
              order.total_cost?.$numberDecimal || order.total_cost || 0;
            const status = order.order_status;
            const statusColor = status === "completed" ? "#12B76A" : "#F79009";
            const statusBg = status === "completed" ? "#E8F5E9" : "#FFF3E0";

            return (
              <div
                key={order.order_id || idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 12px",
                  marginBottom: 0,
                  background: "#fff",
                  borderRadius: 0,
                  minHeight: 62,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.01)",
                  cursor: "pointer",
                  borderBottom: idx !== orders.length - 1 ? "1px solid #eee" : "none",
                }}
                onClick={() => navigate(`/app/orders/confirm/${order.order_id}`)}
              >
                <Avatar
                  src={order.customer_profile_image || "/assets/images/avatars/avatar_1.jpg"}
                  alt={order.customer_name}
                  style={{ width: 38, height: 38, marginRight: 19 }}
                />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#222" }}>
                    {order.customer_name}
                  </div>
                  <div style={{ color: "#757575", fontSize: 13 }}>{order.order_number}</div>
                </div>
                <div
                  style={{
                    background: statusBg,
                    color: statusColor,
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 7,
                    padding: "2px 10px",
                    minWidth: 60,
                    textAlign: "center",
                    marginLeft: 0,
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
                <div
                  style={{
                    color: "#222",
                    fontWeight: 700,
                    fontSize: 15,
                    marginLeft: 18,
                    minWidth: 60,
                    textAlign: "right",
                  }}
                >
                  ₦{Number(totalCost).toLocaleString()}
                </div>
              </div>
            );
          })
        )}
        {/* Pagination removed */}
      </div>

      <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
          padding: 24,
          minWidth: 280,
          maxWidth: '90vw',
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Filter</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 14, color: '#757575' }}>Status</label>
              <Select
                fullWidth
                size="small"
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                displayEmpty
                style={{ marginTop: 4 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="abandoned">Abandoned</MenuItem>
              </Select>
            </div>
            <div>
              <label style={{ fontSize: 14, color: '#757575' }}>Order Confirmation</label>
              <Select
                fullWidth
                size="small"
                value={filters.order_confirmation}
                onChange={e => handleFilterChange('order_confirmation', e.target.value)}
                displayEmpty
                style={{ marginTop: 4 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="unconfirmed">Unconfirmed</MenuItem>
              </Select>
            </div>
            <div>
              <label style={{ fontSize: 14, color: '#757575' }}>Payment Method</label>
              <Select
                fullWidth
                size="small"
                value={filters.payment_method}
                onChange={e => handleFilterChange('payment_method', e.target.value)}
                displayEmpty
                style={{ marginTop: 4 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
                <MenuItem value="wallet">Wallet</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Min Total Cost</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.min_total_cost}
                  onChange={e => handleFilterChange('min_total_cost', e.target.value)}
                  placeholder="Min"
                  inputProps={{ min: 0 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Max Total Cost</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.max_total_cost}
                  onChange={e => handleFilterChange('max_total_cost', e.target.value)}
                  placeholder="Max"
                  inputProps={{ min: 0 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Min Order Unit</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.min_order_unit}
                  onChange={e => handleFilterChange('min_order_unit', e.target.value)}
                  placeholder="Min"
                  inputProps={{ min: 0 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Max Order Unit</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.max_order_unit}
                  onChange={e => handleFilterChange('max_order_unit', e.target.value)}
                  placeholder="Max"
                  inputProps={{ min: 0 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Start Date</label>
                <DatePicker
                  selected={filters.startDate}
                  onChange={date => handleFilterChange('startDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Start"
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>End Date</label>
                <DatePicker
                  selected={filters.endDate}
                  onChange={date => handleFilterChange('endDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="End"
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button onClick={handleApplyFilters} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#af3585', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Apply</button>
              <button onClick={handleResetFilters} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #E0E0E0', background: '#fff', color: '#af3585', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Reset</button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default OrdersPage;
